/**
 * @fileoverview PodClaimNotesFeed — thin container wiring the shared
 * `NotesFeedView` to the **POD claim** notes endpoints
 * (`api.jobs.recoveries.notes.*`).
 *
 * Mirrors `ContactNotesFeed` and `TaskNotesFeed`: the container owns the
 * data and the caller owns the layout — here the claim dialog's Notes tab.
 * The only POD-specific part is the composer prompt. The AI summary surface
 * is hidden (`showSummary={false}`); the adjudication draft is the AI
 * affordance on this record.
 *
 * Used by: src/components/jobs/phases/recoveries/PodClaimPanel.tsx
 */

import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { NotesFeedView } from "@/components/contacts/notes/NotesFeedView";
import type { FeedNote } from "@/components/contacts/notes/NoteRow";

export function PodClaimNotesFeed({
  claimId,
  currentUserId,
}: {
  claimId: Id<"podClaims">;
  currentUserId?: Id<"users">;
}) {
  const { data: notes, isLoading } = useQuery({
    ...convexQuery(api.jobs.recoveries.notes.listPodClaimNotes, { claimId }),
  });

  const createNote = useConvexMutation(
    api.jobs.recoveries.notes.createPodClaimNote,
  );
  const updateNote = useConvexMutation(
    api.jobs.recoveries.notes.updatePodClaimNote,
  );
  const deleteNote = useConvexMutation(
    api.jobs.recoveries.notes.deletePodClaimNote,
  );

  const handlePost = useCallback(
    async (content: string) => {
      await createNote({ claimId, content });
    },
    [claimId, createNote],
  );

  const handleSaveNote = useCallback(
    async (noteId: string, content: string) => {
      await updateNote({ noteId: noteId as Id<"podClaimNotes">, content });
    },
    [updateNote],
  );

  const handleDeleteNote = useCallback(
    async (noteId: string) => {
      await deleteNote({ noteId: noteId as Id<"podClaimNotes"> });
    },
    [deleteNote],
  );

  const notesList: Array<FeedNote> = useMemo(
    () =>
      (notes ?? []).map((n) => ({
        _id: n._id,
        content: n.content,
        createdBy: n.createdBy,
        createdAt: n.createdAt,
        updatedAt: n.updatedAt,
        authorDisplayName: n.authorDisplayName,
        authorAvatarUrl: n.authorAvatarUrl ?? undefined,
        lastEditedByName: n.lastEditedByName ?? undefined,
      })),
    [notes],
  );

  return (
    <NotesFeedView
      notes={notesList}
      notesCount={notesList.length}
      isLoading={isLoading}
      currentUserId={currentUserId}
      showSummary={false}
      placeholder="What did you check, what did you ask the creditor for, what is still outstanding?"
      onPost={handlePost}
      onSaveNote={handleSaveNote}
      onDeleteNote={handleDeleteNote}
    />
  );
}
